'use client';

import { useMemo, useState } from 'react';

import { Maybe } from 'graphql/jsutils/Maybe';

import BrandCard from '@/modules/brand/components/BrandCard';
import CategoryMultiSelect from '@/modules/brand/components/CategoryMultiSelect';
import SkeletonCard from '@/modules/brand/components/SkeletonCard';
import { Button } from '@/modules/common/components/Button';
import { Search } from '@/modules/common/components/icons';
import { Input } from '@/modules/common/components/ui/input';
import useIsMobile from '@/modules/common/hooks/useIsMobile';

import { BrandPage } from '@/gql/__generated/graphql.types';

import { useBrandPageCollectionAllWithDiscountQuery } from '../gql/__generated/brandPageCollection.generated';

export type SafeBrandPage = NonNullable<Maybe<BrandPage>>;

export function BrandDiscountPageTemplate() {
  const isMobile = useIsMobile();
  const quantityToShow = isMobile ? 6 : 12;

  const [searchTerm, setSearchTerm] = useState('');
  const [itemsToShow, setItemsToShow] = useState(quantityToShow);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const { data: allBrandsData, isLoading } = useBrandPageCollectionAllWithDiscountQuery();

  const allBrands = useMemo(() => {
    return (
      allBrandsData?.brandPageCollection?.items?.filter((item): item is SafeBrandPage => !!item) ||
      []
    );
  }, [allBrandsData]);

  const filteredBrands = useMemo(() => {
    let result = allBrands;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((brand) => brand.brand?.toLowerCase().includes(searchLower));
    }

    if (selectedCategories.length > 0) {
      result = result.filter((brand) =>
        brand.category?.some((cat) => selectedCategories.includes(cat as string))
      );
    }

    return result;
  }, [allBrands, searchTerm, selectedCategories]);

  const visibleBrands = filteredBrands.slice(0, itemsToShow);

  const handleSeeMore = () => {
    setItemsToShow((prev) => prev + quantityToShow);
  };

  const hasMore = itemsToShow < filteredBrands.length;

  const categoryOptions = useMemo(() => {
    const categoriesSet = new Set<string>();
    allBrands.forEach((brand) => {
      brand.category?.forEach((cat) => categoriesSet.add(cat as string));
    });
    return Array.from(categoriesSet).sort();
  }, [allBrands]);

  return (
    <section className="pt-12 lg:pt-20">
      <div className="container px-4 pb-12 lg:px-8">
        <div className="max-w-[890px]">
          <h1 className="text-[42px] font-bold text-black md:text-6xl">
            Participating Trade Pro Brands.
          </h1>
          <p className="mt-4 max-w-[834px] text-xl text-black">
            As a DesignShop Pro, you enjoy exclusive savings when purchasing from the brands below.
            For assistance please contact us at{' '}
            <a className="underline" href="mailto:tradepro@designshop.com">
              tradepro@designshop.com
            </a>
          </p>
        </div>
      </div>
      <div className="bg-[#EFECE9] py-12 pb-12">
        <div className="container px-4 lg:px-8">
          <div className="flex w-full flex-col items-center justify-between gap-8 lg:flex-row lg:gap-0">
            <CategoryMultiSelect
              options={categoryOptions}
              selected={selectedCategories}
              onChange={setSelectedCategories}
            />

            <div className="relative w-full max-w-[680px]">
              <Input
                type="text"
                placeholder="Search Brands"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="text-xl placeholder:font-light placeholder:text-[#BBB9BA] focus:outline-none lg:text-5xl"
              />
              <Search className="absolute right-3 top-1/2 h-12 w-12 -translate-y-1/2 text-[#BBB9BA]" />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 py-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {isLoading ?
              Array.from({ length: quantityToShow }).map((_, i) => <SkeletonCard key={i} />)
            : visibleBrands.map((brand) => <BrandCard key={brand.brandId} brand={brand} />)}
          </div>
          {!isLoading && hasMore && (
            <div className="flex w-full items-center justify-center pb-8">
              <Button onClick={handleSeeMore}>See More</Button>
            </div>
          )}
          <p className="text-sm text-[#86868A]">
            * The discount shown is the minimum available. Larger orders may qualify for additional
            discounts. Please contact the brand for more details.
          </p>
        </div>
      </div>
    </section>
  );
}
